import { useState, useEffect } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DownloadIcon from '@mui/icons-material/GetApp'
import { Accordion, AccordionSummary, AccordionDetails, Divider, Typography } from '@mui/material'
import { RouteComponentProps } from 'react-router-dom'
import { CvsAvatar, DatasetStatus, DatasetTracker, MotionDivContainer } from '@components'
import type { UserPartial } from '@components/CvsAvatar'
import { Loader } from '@components/loader'
import { useSettings } from '@datasets/dataset-details/hooks/useSettings'
import { useTemplate } from '@global/hooks/useTemplate'
import { Submission } from '@global/types'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { formatDate } from './utils'
import { Error, Executions, Targets, Trigger, StatusCircle } from './components'
import {
  IDatasetIntegrationExecution,
  IDatasetIntegrationExecutions,
} from '../datasetHistoryV2/types'

import '../dataset-details.global.css'
import * as css from './styles'
import { useQueryPendingRollupDataset } from '../../../templates/hooks/useQueryPending'
import { t } from '@lingui/core/macro'
import { requestAssignment } from '../datasetHistoryV2/api'

interface TokenResponse {
  token: string
}

interface SubmissionAccordionProps {
  submission: Submission
  allSubmissions: Submission[]
  datasetId: number
  setSubmissions: (submissions: Submission[]) => void
  executions: Record<number, IDatasetIntegrationExecutions>
  loading: boolean
  setExecutions: React.Dispatch<React.SetStateAction<Record<number, IDatasetIntegrationExecutions>>>
  setLoading: (loading: boolean) => void
}

const SubmissionAccordion = ({
  submission,
  datasetId,
  setSubmissions,
  executions,
  allSubmissions,
  loading,
  setExecutions,
  setLoading,
}: SubmissionAccordionProps) => {
  const hasDatasetPosts = Boolean(submission?.dataset_posts?.length)
  const hasExecutions = Boolean(executions?.[submission?.id]) // FYI when an execution is re-run, the submission id changes, and then this becomes false because we don't have the latest executions object!
  const hasInternalErrors = Boolean(
    (submission.status !== DatasetStatus.COMPLETE && submission?.errors?.length) ||
      submission?.errors?.some(err => err.type_code >= 5000 && err.type_code < 6000),
  )
  const hasDeletedFieldsError = Boolean(submission?.errors?.some(err => err.type_code == 4500))
  const hasErrors = hasInternalErrors || hasDeletedFieldsError
  const showDivider = hasDatasetPosts || hasExecutions || hasErrors
  const { requireApproval } = useSettings()
  const API_DEBUG_ENABLED = useAppSelector(
    state => state.Auth.features['dataset-details-api-debug'],
  )
  const meta = useAppSelector(state => state.Datasets.datasetMetadataById[submission.dataset_id])
  const { template } = useTemplate({ templateId: meta?.template_id })

  return (
    <Accordion key={submission.id}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <CvsAvatar user={submission.submitted_by_user as UserPartial} />
        <css.SubmittedByDetails>
          <Typography>{submission.submitted_by_user?.name}</Typography>
          <Typography variant="caption">
            {t`Submitted on:`}
            {formatDate(submission.created_at)}
          </Typography>
        </css.SubmittedByDetails>
        <css.ActionIcons>
          {API_DEBUG_ENABLED && (
            <DownloadIcon css={css.icon} onClick={() => void downloadSubmissionZip(submission)} />
          )}
          {submission?.trigger && <Trigger trigger={submission.trigger} />}
          <StatusCircle status={submission.status} />
        </css.ActionIcons>
      </AccordionSummary>
      <AccordionDetails css={css.flexColumn}>
        <DatasetTracker
          submission={submission}
          requireApproval={requireApproval}
          creativeClassificationEnabled={Boolean(
            template?.creativeConfiguration?.length ||
              template?.template_fields?.some(
                templateField => templateField?.field?.governance_type === 'creative',
              ),
          )}
        />
        {showDivider && <Divider orientation="horizontal" css={css.divider} />}
        {hasErrors && submission?.errors?.map(error => <Error key={error.id} error={error} />)}
        {hasDatasetPosts && (
          <css.Section>
            <Typography variant="h6" gutterBottom>
              {t`Targets`}
            </Typography>
            <Targets
              submission={submission}
              rerunDatasetPost={(...args) => void rerunDatasetPost(...args)}
              downloadPostZip={(...args) => void downloadPostZip(...args)}
            />
          </css.Section>
        )}
        {hasExecutions && (
          <css.Section>
            <Typography variant="h6" gutterBottom>
              {t`Integration output`}
            </Typography>
            <Executions
              loading={loading}
              submission={submission}
              executions={executions?.[submission?.id] ?? {}}
              refreshGAIntegrationStatus={(...args) => void refreshGAIntegrationStatus(...args)}
              downloadIntegration={(...args) => void downloadIntegration(...args)}
              rerunIntegration={(...args) => void rerunIntegration(...args)}
            />
          </css.Section>
        )}
      </AccordionDetails>
    </Accordion>
  )

  async function downloadSubmissionZip(sub: Submission) {
    const { dataset_id: dsId, id: subId } = sub

    const { data } = await api.get<TokenResponse>(`/api/v1/account/dataset/${dsId}/csv/token`)
    const location = `/api/v1/account/dataset/${dsId}/submissions/${subId}/apizip/${data.token}`
    window.location.href = location
  }

  async function rerunIntegration(execution: IDatasetIntegrationExecution, sub: Submission) {
    const { id: execId } = execution
    const { id: subId } = sub

    setLoading(true)

    await api.post(`/api/v1/account/dataset/submission/triggerOutputConfigurations`, {
      submissionIds: [subId],
      integrationExecutionIds: [execId],
    })
    const data = await requestAssignment(subId)

    setExecutions(prev => ({
      ...prev,
      [subId]: data,
    }))

    setLoading(false)

    // FYI when this runs, the UI will not show the new execution until the next refresh of the page
  }

  async function refreshGAIntegrationStatus(execution: IDatasetIntegrationExecution) {
    const { id: execId } = execution

    setLoading(true)

    try {
      const { data } = await api.post('/api/v1/integration-assignments/execution/refresh/job', {
        execution_id: execId,
        submission_id: execution.Submission,
      })

      setExecutions(prev => ({
        ...prev,
        [execution.Submission]: {
          ...prev[execution.Submission],
          [execId]: data,
        },
      }))
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }

  async function rerunDatasetPost(sub: Submission, post: any) {
    const { id: postId } = post
    const { id: subId, dataset_posts } = sub

    await api.post(`/api/v1/account/dataset/submission/${subId}/${postId}/run`)
    const submissionIndex = allSubmissions.findIndex(s => s.id === subId)
    const postIndex = dataset_posts?.findIndex(p => p.id === postId) ?? -1
    const newSubmissions = allSubmissions.reduce<Submission[]>((memo, item, index) => {
      if (index === submissionIndex && item.dataset_posts) {
        item.dataset_posts.splice(postIndex, 1, { ...post, status: DatasetStatus.PENDING })
      }
      memo.push(item)
      return memo
    }, [])
    setSubmissions(newSubmissions)
  }

  async function downloadPostZip(post: any) {
    const { data } = await api.get<TokenResponse>(`/api/v1/account/dataset/${datasetId}/csv/token`)
    const location = `/api/v1/account/dataset/${datasetId}/submissions/${post.dataset_submission_id}/apizip/${data.token}?postId=${post.id}`
    window.location.href = location
  }

  async function downloadIntegration(execution: IDatasetIntegrationExecution) {
    const { data } = await api.post<{ file: number }, { data: string }>(
      '/api/v1/integration-assignments/execution/file/',
      {
        file: execution.id,
      },
    )
    window.location.href = data
  }
}

interface DatasetHistoryParams {
  id: string
}
export const DatasetHistory = ({ match }: RouteComponentProps<DatasetHistoryParams>) => {
  const dsId = Number(match.params.id)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [executions, setExecutions] = useState<Record<number, IDatasetIntegrationExecutions>>({})
  const [loading, setLoading] = useState(true)

  const { pendingRollupDataset, isLoading: isLoadingPending } = useQueryPendingRollupDataset({
    datasetId: String(dsId),
  })

  useEffect(() => {
    const withParams = [
      'submitted_by_user:id,avatar_slug,name',
      'dataset_posts',
      'dataset_posts.target',
      'dataset_posts.multi_target',
      'errors',
    ]
      .map(param => `with[]=${param}`)
      .join('&')

    const url = `/api/v1/account/dataset/${dsId}/submissions?${withParams}`

    async function fetchData() {
      const { data } = await api.get<Submission[]>(url)

      const latestSubmissions = pendingRollupDataset?.submissions || data

      setSubmissions(latestSubmissions)
      data.forEach(sub => {
        void loadAssignment(sub.id)
      })
      setLoading(false)
    }

    void fetchData()
  }, [pendingRollupDataset])

  if (loading || isLoadingPending) {
    return <Loader />
  }

  return (
    <MotionDivContainer className="DatasetHistory">
      <>
        {submissions.map(sub => (
          <SubmissionAccordion
            key={sub.id}
            submission={sub}
            datasetId={dsId}
            executions={executions}
            loading={loading}
            setLoading={setLoading}
            setExecutions={setExecutions}
            setSubmissions={setSubmissions}
            allSubmissions={submissions}
          />
        ))}
      </>
    </MotionDivContainer>
  )

  async function loadAssignment(id: number) {
    const data = await requestAssignment(id)

    if (Object.values(data).length) {
      setExecutions(prev => ({
        ...prev,
        [id]: data,
      }))
    }
  }
}
